import { useEffect, useMemo, useState } from 'react';
import {
  loadRetentionLog,
  loadUnsubCount,
  runRetentionWave,
  type WaveResult,
} from '../../lib/retentionEmailService';
import {
  retentionStats,
  wavesByDay,
  nextWaveDate,
  variantLabel,
  AUDIENCES,
  type RetentionLogRow,
  type RetentionAudience,
} from '../../lib/retentionEmail';

/**
 * « Rétention email » — pilotage des rappels NON-PROMOTIONNELS (Big Burger) :
 *   • KPIs : emails envoyés, erreurs, destinataires uniques, désabonnements + taux ;
 *   • répartition par variante (A/B rotation) ;
 *   • historique des vagues (par jour) ;
 *   • prochaine vague automatique (cron 1er & 15) ;
 *   • lancement manuel par audience — aperçu (dry-run) puis envoi confirmé.
 * Rappel conformité : soft opt-in, désinscription en 1 clic, garde-fou de cadence
 * (aucun client re-sollicité avant 12 jours), aucune promotion.
 */

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('fr-FR');
};
const fmtDateTime = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

export default function RetentionEmailSection() {
  const [rows, setRows] = useState<RetentionLogRow[]>([]);
  const [unsub, setUnsub] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<RetentionAudience | null>(null);
  const [confirm, setConfirm] = useState<RetentionAudience | null>(null);
  const [result, setResult] = useState<Record<string, WaveResult>>({});

  const refresh = async () => {
    const [log, u] = await Promise.all([loadRetentionLog(), loadUnsubCount()]);
    if (log.ok) { setRows(log.rows); setError(null); }
    else setError(log.error ?? 'Chargement impossible.');
    setUnsub(u < 0 ? 0 : u);
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const stats = useMemo(() => retentionStats(rows), [rows]);
  const waves = useMemo(() => wavesByDay(rows).slice(0, 12), [rows]);
  const maxWave = Math.max(1, ...waves.map((w) => w.sent + w.errors));
  const nextWave = useMemo(() => nextWaveDate(), []);
  const unsubRate = stats.uniqueRecipients > 0 ? (unsub / stats.uniqueRecipients) * 100 : 0;
  const recent = rows.slice(0, 25);

  const doDry = async (audience: RetentionAudience) => {
    setBusy(audience);
    const res = await runRetentionWave({ audience, dryRun: true });
    setResult((r) => ({ ...r, [audience]: res }));
    setBusy(null);
  };
  const doSend = async (audience: RetentionAudience) => {
    setConfirm(null);
    setBusy(audience);
    const res = await runRetentionWave({ audience, dryRun: false });
    setResult((r) => ({ ...r, [audience]: res }));
    setBusy(null);
    if (res.ok) await refresh();
  };

  return (
    <div className="max-w-5xl">
      <div className="mb-4">
        <h1 className="text-xl font-bold">Rétention email</h1>
        <p className="text-sm opacity-70">
          Rappels non-promotionnels (Big Burger) pour ramener les clients vers le site.
          Soft opt-in, désinscription 1 clic, pas de re-sollicitation avant 12 jours. Aucune promo.
        </p>
      </div>

      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{error}</div>
      ) : loading ? (
        <div className="py-6 text-center text-sm text-slate-400">Chargement…</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
              <div className="text-xs font-bold uppercase text-indigo-700">📧 Emails envoyés</div>
              <div className="mt-1 text-2xl font-black text-indigo-800">{stats.totalSent}</div>
              <div className="text-xs text-indigo-700/80">{stats.totalErrors} erreur(s)</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-xs font-bold uppercase text-slate-500">👤 Destinataires uniques</div>
              <div className="mt-1 text-2xl font-black text-slate-900">{stats.uniqueRecipients}</div>
              <div className="text-xs text-slate-500">clients touchés au moins 1 fois</div>
            </div>
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
              <div className="text-xs font-bold uppercase text-rose-700">🚫 Désabonnements</div>
              <div className="mt-1 text-2xl font-black text-rose-800">{unsub}</div>
              <div className="text-xs text-rose-700/80">{unsubRate.toFixed(1)} % des destinataires</div>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-xs font-bold uppercase text-emerald-700">📅 Prochaine vague auto</div>
              <div className="mt-1 text-2xl font-black text-emerald-800">{nextWave.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</div>
              <div className="text-xs text-emerald-700/80">cron 1er &amp; 15 · dernière : {fmtDate(stats.lastWaveAt)}</div>
            </div>
          </div>

          {/* Répartition par variante */}
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">🔀 Répartition par variante</div>
            {stats.byVariant.length === 0 ? (
              <div className="text-sm text-slate-400">Aucun envoi pour l’instant.</div>
            ) : (
              <div className="flex flex-col gap-2">
                {stats.byVariant.map((v) => {
                  const max = Math.max(1, ...stats.byVariant.map((x) => x.sent));
                  return (
                    <div key={v.key} className="flex items-center gap-3">
                      <span className="w-56 shrink-0 truncate text-xs font-semibold text-slate-600" title={v.label}>{v.label}</span>
                      <span className="h-4 flex-1 overflow-hidden rounded bg-slate-100">
                        <span className="block h-full bg-indigo-400" style={{ width: `${(v.sent / max) * 100}%` }} />
                      </span>
                      <span className="w-24 shrink-0 text-right text-sm font-bold text-slate-800">
                        {v.sent}{v.errors > 0 && <span className="ml-1 text-[11px] font-semibold text-rose-500">+{v.errors} err</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Historique des vagues par jour */}
          {waves.length > 0 && (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">📈 Historique des vagues</div>
              <div className="flex flex-col gap-2">
                {waves.map((w) => (
                  <div key={w.day} className="flex items-center gap-3">
                    <span className="w-28 shrink-0 text-xs font-semibold text-slate-600">{fmtDate(w.day)}</span>
                    <span className="h-4 flex-1 overflow-hidden rounded bg-slate-100">
                      <span className="flex h-full">
                        <span className="h-full bg-emerald-400" style={{ width: `${(w.sent / maxWave) * 100}%` }} />
                        <span className="h-full bg-rose-400" style={{ width: `${(w.errors / maxWave) * 100}%` }} />
                      </span>
                    </span>
                    <span className="w-24 shrink-0 text-right text-sm font-bold text-slate-800">{w.sent}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lancement manuel */}
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">🚀 Lancer une vague maintenant</div>
            <p className="mb-3 text-xs text-slate-400">
              Fais d’abord un <b>Aperçu</b> (aucun email envoyé) pour voir le volume, puis confirme l’envoi.
              Les clients sollicités il y a moins de 12 jours et les désabonnés sont automatiquement exclus.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {AUDIENCES.map((a) => {
                const res = result[a.key];
                return (
                  <div key={a.key} className="rounded-xl border border-slate-200 p-3">
                    <div className="text-sm font-bold text-slate-800">{a.label}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{a.hint}</div>
                    <div className="mt-2 flex gap-1">
                      <button
                        onClick={() => doDry(a.key)}
                        disabled={busy === a.key}
                        className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
                      >
                        {busy === a.key ? '…' : '👁 Aperçu'}
                      </button>
                      {confirm === a.key ? (
                        <>
                          <button
                            onClick={() => doSend(a.key)}
                            disabled={busy === a.key}
                            className="rounded-lg bg-red-600 px-2 py-1 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            Confirmer l’envoi
                          </button>
                          <button
                            onClick={() => setConfirm(null)}
                            className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold"
                          >
                            Annuler
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setConfirm(a.key)}
                          disabled={busy === a.key}
                          className="rounded-lg bg-indigo-600 px-2 py-1 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                          📧 Envoyer
                        </button>
                      )}
                    </div>
                    {res && (
                      <div className={`mt-2 text-[11px] ${res.ok ? 'text-slate-600' : 'text-rose-600'}`}>{res.message}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Journal récent */}
          <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-4">
            <div className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">🧾 Derniers envois</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <th className="px-2 py-2">Date</th>
                  <th className="px-2 py-2">Email</th>
                  <th className="px-2 py-2">Variante</th>
                  <th className="px-2 py-2">Statut</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100">
                    <td className="px-2 py-2 text-slate-500">{fmtDateTime(r.sent_at)}</td>
                    <td className="px-2 py-2 font-medium text-slate-700">{r.email ?? '—'}</td>
                    <td className="px-2 py-2 text-slate-600">{variantLabel(r.variant_key)}</td>
                    <td className="px-2 py-2">
                      {r.status === 'sent' ? (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">envoyé</span>
                      ) : (
                        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700" title={r.error_message ?? ''}>erreur</span>
                      )}
                    </td>
                  </tr>
                ))}
                {recent.length === 0 && (
                  <tr><td colSpan={4} className="px-2 py-6 text-center text-slate-400">Aucun envoi enregistré.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
